import React from 'react';

import { formatMoney } from '@betterment/core';
import { Box, HStack, Icon, ShimmerText, Text, VStack } from '@betterment/samba-react';
import { SambaAutoLink, formatNumberWithDecimals } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { AccountIcon } from './AccountIcon';
import { AccountRowMenu } from './AccountRowMenu';
import { AccountStatusIndicators } from './AccountStatusIndicators';
import { DriftInfo } from './DriftInfo';
import { HouseholdAccountsQuery } from './graphql/query.householdAccounts.generated';
import { HouseholdAccountYtdReturnsQuery } from './graphql/query.householdAccountYtdReturns.generated';
import { RowCell } from './RowCell';
import { formatAccountHolders, hasRecurringTransactions } from './utils';

import urlBuilder from '../../../Shared/url-builder';

type EnvelopeAccount = HouseholdAccountsQuery['household']['accounts'][number];
type SubAccount = Extract<EnvelopeAccount, { __typename: 'ManagedAccount' }>['subAccounts'][number];
type YtdReturnsData = HouseholdAccountYtdReturnsQuery['household']['accountYtdReturns'];

const TCPSubAccountRow: React.FC<{
  subAccount: SubAccount;
  isLastRow: boolean;
  showYtdReturns: boolean;
  isWeightedReturnsEnabled: boolean;
}> = ({ subAccount, isLastRow, showYtdReturns }) => {
  const paddingBottom = isLastRow ? 'space.400' : 'space.150';
  const borderCss = isLastRow
    ? ''
    : {
        borderBottomWidth: 'thin',
        borderBottomStyle: 'solid',
        borderBottomColor: 'border.weak',
      };

  const gridColumns = showYtdReturns
    ? 'minmax(auto, 140px) 1fr 1fr minmax(auto, 250px) minmax(auto, 190px) 32px'
    : 'minmax(auto, 140px) 1fr 1fr minmax(auto, 150px) 32px';

  return (
    <Box
      key={subAccount.id}
      css={{
        display: 'grid',
        gridColumn: '1 / -1',
        gridTemplateColumns: gridColumns,
      }}
    >
      <RowCell />
      <RowCell
        css={{
          paddingTop: 'space.150',
          paddingBottom,
          paddingLeft: 'space.600',
          ...borderCss,
        }}
      >
        <Text color="text.weak" variant="text50">
          {subAccount.name}
        </Text>
      </RowCell>
      <RowCell css={{ ...borderCss }} />
      {showYtdReturns && <RowCell css={{ ...borderCss }} />}
      <RowCell
        css={{
          paddingTop: 'space.150',
          paddingBottom,
          justifyContent: 'end',
          ...borderCss,
        }}
      >
        <Text color="text.weak" variant="text50">
          {formatMoney(subAccount.balance)}
        </Text>
      </RowCell>
      <RowCell css={{ ...borderCss }} />
      <RowCell />
    </Box>
  );
};

export const AccountRow: React.FC<{
  account: EnvelopeAccount;
  ytdReturnsData?: YtdReturnsData;
  ytdReturnsLoading?: boolean;
  showYtdReturns: boolean;
  returnType: 'twr' | 'mwr';
  isWeightedReturnsEnabled: boolean;
}> = ({ account, ytdReturnsData, ytdReturnsLoading, showYtdReturns, returnType, isWeightedReturnsEnabled }) => {
  const iconUrl = account.icon.light;
  const householdId = useRequiredParam('householdId');
  const isTCP = account.__typename === 'ManagedAccount' && account.subAccounts && account.subAccounts.length > 1;
  const portfolio = account.__typename === 'ManagedAccount' ? account.portfolio : null;
  const hasReccuringTransactionConfigs = hasRecurringTransactions(account);
  const accountHolders = formatAccountHolders(account.accountHolders);

  const ytdReturn = ytdReturnsData?.find(ytd => ytd.accountId === account.id)?.ytdReturn;

  let selectedPercent: number | null | undefined;
  if (isWeightedReturnsEnabled) {
    selectedPercent = returnType === 'twr' ? ytdReturn?.timeWeighted : ytdReturn?.moneyWeighted;
  } else {
    selectedPercent = ytdReturn?.moneyWeighted;
  }

  const gridColumns = showYtdReturns
    ? 'minmax(auto, 140px) 1fr 1fr minmax(auto, 250px) minmax(auto, 190px) 32px'
    : 'minmax(auto, 140px) 1fr 1fr minmax(auto, 200px) 32px';

  const css = {
    display: 'grid',
    gridTemplateColumns: gridColumns,
    gridAutoRows: 'auto',
    color: 'text',
    paddingTop: 'space.300',
    paddingBottom: isTCP ? '0' : 'space.300',
    borderTop: 'solid',
    borderWidth: 'thin',
    borderColor: 'border.weak',
    minHeight: '112px',
  };

  return (
    <SambaAutoLink
      key={account.id}
      to={urlBuilder.getHouseholdAccountPage.absolute({ householdId, accountId: account.id })}
      css={{
        width: '100%',
        gridColumn: '1 / -1',
        textDecoration: 'none',
        _hover: {
          textDecoration: 'none',
          color: 'inherit',
        },
        '&:hover:not(:has(.no-row-hover:hover)) .account-title': {
          textDecoration: 'underline',
        },
      }}
      aria-label={`${account.name}`}
    >
      <Box css={{ ...css }} role="row">
        <RowCell css={{ gridColumn: '1' }}>
          <Box>
            <Text>{accountHolders}</Text>
          </Box>
        </RowCell>
        <RowCell css={{ gridColumn: '2' }}>
          <HStack css={{ alignItems: 'center', gap: 'space.150' }}>
            <AccountIcon accountIconUrl={iconUrl} envelopeIconUrl={account.envelope.purpose?.icon?.light} />
            <VStack>
              {account.envelope.purpose && (
                <Text color="text.weak" variant="text50">
                  {account.envelope.purpose.name}
                </Text>
              )}
              <Text color="text" className="account-title">
                {account.name}
              </Text>
              <AccountStatusIndicators account={account} />
            </VStack>
          </HStack>
        </RowCell>
        <RowCell css={{ gridColumn: '3' }}>
          {portfolio ? (
            <VStack>
              <Text variant="text100">
                {portfolio.strategyName} - {portfolio.targetAllocationDescription}
              </Text>
              <Text color="text.weak" variant="text50">
                Rebalance: {portfolio.rebalance.isEnabled ? 'On' : 'Off'}
              </Text>
              <DriftInfo managedAccountId={account.id} />
            </VStack>
          ) : (
            <Text>-</Text>
          )}
        </RowCell>
        {showYtdReturns && (
          <RowCell
            css={{
              gridColumn: '4',
              justifyContent: 'end',
              paddingRight: isWeightedReturnsEnabled ? 'space.400' : 'space.300',
            }}
          >
            {ytdReturnsLoading && <ShimmerText textStyle="text100" css={{ maxWidth: '80%' }} />}
            {!ytdReturnsLoading &&
              ytdReturn &&
              (isWeightedReturnsEnabled ? (
                <Text
                  variant="text100"
                  color={selectedPercent != null && selectedPercent > 0 ? 'text.success' : 'text'}
                >
                  {selectedPercent != null &&
                  account.__typename !== 'DirectTradingAccount' &&
                  account.__typename !== 'CashAccount'
                    ? `${selectedPercent > 0 ? '+' : ''}${formatNumberWithDecimals(selectedPercent, 2)}%`
                    : '-'}
                </Text>
              ) : (
                <HStack css={{ alignItems: 'center', gap: 'space.100', justifyContent: 'flex-end' }}>
                  {ytdReturn.amount != null && (
                    <Text variant="text100" color={ytdReturn.amount.cents > 0 ? 'text.success' : 'text'}>
                      {ytdReturn.amount.cents > 0 ? '+' : ''}
                      {formatMoney(ytdReturn.amount)}
                    </Text>
                  )}
                  <Text
                    variant="text100"
                    color={selectedPercent != null && selectedPercent > 0 ? 'text.success' : 'text'}
                  >
                    {selectedPercent != null
                      ? `(${selectedPercent > 0 ? '+' : ''}${formatNumberWithDecimals(selectedPercent, 2)}%)`
                      : '-'}
                  </Text>
                </HStack>
              ))}
            {!ytdReturnsLoading && !ytdReturn && <Text color="text.weak">-</Text>}
          </RowCell>
        )}
        <RowCell css={{ gridColumn: showYtdReturns ? '5' : '4', justifyContent: 'end', paddingRight: 'space.300' }}>
          <VStack css={{ alignItems: 'end' }}>
            <HStack css={{ alignItems: 'center', gap: 'space.50' }}>
              {hasReccuringTransactionConfigs && (
                <Icon name="ArrowCircle" size="xsmall" aria-label="Has Recurring Transaction" />
              )}
              <Text>{formatMoney(account.balance)}</Text>
            </HStack>
            {isWeightedReturnsEnabled && ytdReturnsLoading && (
              <ShimmerText textStyle="text100" css={{ width: '150px' }} />
            )}
            {isWeightedReturnsEnabled && ytdReturn?.amount != null && (
              <Text variant="text100" color={ytdReturn.amount.cents > 0 ? 'text.success' : 'text'}>
                {ytdReturn.amount.cents > 0 ? '+' : ''}
                {formatMoney(ytdReturn.amount)}
              </Text>
            )}
          </VStack>
        </RowCell>
        <RowCell
          className="no-row-hover"
          css={{ gridColumn: showYtdReturns ? '6' : '5', justifyContent: 'end', paddingRight: 'space.0' }}
        >
          <AccountRowMenu account={account} householdId={householdId} showImpersonationModel={false} />
        </RowCell>
        {isTCP &&
          account.subAccounts.map((subAccount, index) => (
            <TCPSubAccountRow
              subAccount={subAccount}
              isLastRow={index === account.subAccounts.length - 1}
              showYtdReturns={showYtdReturns}
              isWeightedReturnsEnabled={isWeightedReturnsEnabled}
            />
          ))}
      </Box>
    </SambaAutoLink>
  );
};
